import React, { useEffect, useRef, useState } from 'react';

const ScrollReveal = ({ 
  children, 
  direction = 'up', 
  delay = 0, 
  duration = 600,
  distance = 30,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [delay]);

  const getTransform = () => {
    if (isVisible) return 'translate3d(0, 0, 0)';
    
    switch (direction) {
      case 'up':
        return `translate3d(0, ${distance}px, 0)`;
      case 'down':
        return `translate3d(0, -${distance}px, 0)`;
      case 'left':
        return `translate3d(${distance}px, 0, 0)`;
      case 'right':
        return `translate3d(-${distance}px, 0, 0)`;
      default:
        return `translate3d(0, ${distance}px, 0)`;
    }
  };

  const animationStyle = {
    transform: getTransform(),
    opacity: isVisible ? 1 : 0,
    transition: `all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
    willChange: 'transform, opacity'
  };

  return (
    <div
      ref={elementRef}
      style={animationStyle}
      className={className}
    >
      {children}
    </div>
  );
};

// Staggered animations for multiple items
export const ScrollRevealContainer = ({ children, stagger = 100, className = '' }) => {
  return (
    <div className={className}>
      {React.Children.map(children, (child, index) => (
        <ScrollReveal delay={index * stagger}>
          {child}
        </ScrollReveal>
      ))}
    </div>
  );
};

// Fade in animation
export const FadeIn = ({ children, delay = 0, duration = 600, className = '' }) => {
  return (
    <ScrollReveal 
      direction="up" 
      delay={delay} 
      duration={duration} 
      distance={20}
      className={className}
    >
      {children}
    </ScrollReveal>
  );
};

// Slide from different directions
export const SlideInLeft = ({ children, delay = 0, duration = 600, className = '' }) => {
  return (
    <ScrollReveal 
      direction="left" 
      delay={delay} 
      duration={duration} 
      distance={50}
      className={className}
    >
      {children}
    </ScrollReveal>
  );
};

export const SlideInRight = ({ children, delay = 0, duration = 600, className = '' }) => {
  return (
    <ScrollReveal 
      direction="right" 
      delay={delay} 
      duration={duration} 
      distance={50}
      className={className}
    >
      {children}
    </ScrollReveal>
  );
};

export const SlideInUp = ({ children, delay = 0, duration = 600, className = '' }) => {
  return (
    <ScrollReveal 
      direction="up" 
      delay={delay} 
      duration={duration} 
      distance={50}
      className={className}
    >
      {children}
    </ScrollReveal>
  );
};

// Scale animation
export const ScaleIn = ({ children, delay = 0, duration = 600, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [delay]);

  const animationStyle = {
    transform: isVisible ? 'scale(1)' : 'scale(0.8)',
    opacity: isVisible ? 1 : 0,
    transition: `all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
    willChange: 'transform, opacity'
  };

  return (
    <div
      ref={elementRef}
      style={animationStyle}
      className={className}
    >
      {children}
    </div>
  );
};

export default ScrollReveal; 